import { useState } from "react";
import type { AreaPiloto } from "@/types/territorial";
import NoRealData from "@/components/shared/NoRealData";

export interface PilotLayer {
  id: string;
  label: string;
  cor: string;
}

interface PilotSidebarPanelProps {
  area: AreaPiloto;
  quadrasMapeadas: number;
  camadas: PilotLayer[];
  activeLayers: Set<string>;
  onToggleLayer: (id: string) => void;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[10px] text-foreground-500">{label}</span>
      <span className={`text-[10px] font-medium text-foreground-800 text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default function PilotSidebarPanel({ area, quadrasMapeadas, camadas, activeLayers, onToggleLayer }: PilotSidebarPanelProps) {
  const [activeTab, setActiveTab] = useState<"identificacao" | "camadas" | "entorno">("identificacao");
  const local = [area.municipalityName, area.uf].filter(Boolean).join("/") || "—";

  return (
    <div className="w-full h-full bg-white border-r border-background-200/70 flex flex-col shrink-0 overflow-hidden">
      <div className="flex border-b border-background-200/70">
        {[
          { id: "identificacao" as const, label: "Identificação", icon: "ri-information-line" },
          { id: "camadas" as const, label: "Camadas", icon: "ri-stack-line" },
          { id: "entorno" as const, label: "Entorno", icon: "ri-plant-line" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "text-primary-600 border-b-2 border-primary-500 bg-primary-50/50"
                : "text-foreground-500 hover:text-foreground-700 hover:bg-background-50"
            }`}
          >
            <i className={`${tab.icon} text-xs`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {activeTab === "identificacao" && (
          <div className="bg-background-50 rounded-card border border-background-200/70 p-3">
            <h3 className="text-xs font-semibold text-foreground-800 font-heading mb-2 flex items-center gap-1.5">
              <i className="ri-map-pin-line text-primary-500 text-xs"></i>
              Identificação da Área
            </h3>
            <div className="space-y-1.5">
              <Row label="Nome" value={area.name} />
              <Row label="Código" value={area.code ?? "—"} mono />
              <Row label="Bairro" value={area.neighborhood ?? "—"} />
              <Row label="Município" value={local} />
              <Row
                label="Quadras"
                value={area.expectedBlockCount != null ? `${quadrasMapeadas} de ${area.expectedBlockCount}` : String(quadrasMapeadas)}
              />
              <Row
                label="Área"
                value={area.areaHa != null ? `${area.areaHa.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ha` : "—"}
              />
              <Row label="Status" value={area.status || "—"} />
              <Row
                label="Atualizado em"
                value={area.updatedAt ? new Date(area.updatedAt).toLocaleDateString("pt-BR") : "—"}
              />
            </div>
            <p className="text-[9px] text-foreground-400 mt-2 leading-relaxed">
              Limite preliminar de referência (OSM), não oficial. Quadras vêm da base cadastral promovida para a área piloto.
            </p>
          </div>
        )}

        {activeTab === "camadas" && (
          <div className="space-y-2">
            <p className="text-[10px] text-foreground-500 mb-2">Ative ou desative as camadas de visualização no mapa:</p>
            {camadas.map((camada) => (
              <label key={camada.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-background-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={activeLayers.has(camada.id)}
                  onChange={() => onToggleLayer(camada.id)}
                  className="w-3.5 h-3.5 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                />
                <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: camada.cor }}></div>
                <span className="text-[11px] text-foreground-700">{camada.label}</span>
              </label>
            ))}
          </div>
        )}

        {activeTab === "entorno" && (
          <NoRealData
            icon="ri-landscape-line"
            title="Infraestrutura verde do entorno"
            description="Parques, praças e áreas verdes próximos à área piloto, com distância e direção. Ainda não há camada real de áreas verdes publicada pela API."
          />
        )}
      </div>
    </div>
  );
}
